import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { FileContent } from "src/app/common/components/file-content-list/file-content-list.component";
import { Paged } from "src/app/common/models/paged.model";
import { AbstractService } from "src/app/common/services/abstract.service";
import { ConfigService } from "src/app/common/services/config.service";

const API_PATH = "/api/internal/file-content";

export type FileContentUploadResponse = {
  filename: string;
  filepath: string;
  status: string;
  detail: string;
};

@Injectable({
  providedIn: "root",
})
export class FileContentService extends AbstractService {
  private readonly httpClient = inject(HttpClient);
  private readonly configService = inject(ConfigService);

  async fetchPage(page: number, pageSize: number): Promise<Paged<FileContent> | null> {
    const params = new HttpParams().appendAll({ page, pageSize });
    const source = this.httpClient.get<Paged<FileContent>>(`${API_PATH}/page`, { params });
    return await this.cancelPrevious("fetchPage", source);
  }

  async upload(username: string, files: File[]): Promise<FileContentUploadResponse[]> {
    const apiPath = ` ${this.configService.apiMl.httpUrl}/api/persistence/file_content/upload`;
    const params = new HttpParams().set("username", username).set("store_in_s3", true);
    const formData: FormData = new FormData();
    for (const file of files) {
      formData.append("files", file, file.name);
    }
    return await firstValueFrom(this.httpClient.put<FileContentUploadResponse[]>(apiPath, formData, { params }));
  }

  async delete(id: string): Promise<void> {
    await firstValueFrom(this.httpClient.delete(`${API_PATH}/${id}/delete`));
  }

  async getContent(id: string): Promise<string> {
    return (await firstValueFrom(this.httpClient.get<{ content: string }>(`${API_PATH}/${id}/content`))).content;
  }

  async getNumberOfChunks(id: string): Promise<number> {
    const apiPath = `${this.configService.apiMl.httpUrl}/api/persistence/file_content/count_chunks/${id}`;
    return await firstValueFrom(this.httpClient.get<number>(apiPath));
  }

  async download(s3FilePath: string, filename: string): Promise<void> {
    const apiPath = `${this.configService.apiMl.httpUrl}/api/persistence/file_content/download/${s3FilePath}`;
    try {
      const response = await firstValueFrom(
        this.httpClient.get(apiPath, {
          responseType: "blob",
          observe: "response",
        }),
      );

      if (!response) {
        throw new Error("Empty response body");
      }
      const blob = new Blob([response.body!], {
        type: response.headers.get("content-type") || "application/octet-stream",
      });

      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = filename;
      downloadLink.click();
      window.URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      console.error("File download failed:", error);
      throw error;
    }
  }
}
